// API utility functions

// Get API base URL from environment or use relative URLs for development proxy
export const getApiBaseUrl = () => {
  const envBaseUrl = process.env.REACT_APP_API_BASE_URL;
  if (!envBaseUrl) {
    return '';
  }
  // Never send tunnel/public visitors to localhost (e.g. mobile on dev.ekdantarts.in)
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const isLocalHost = host === 'localhost' || host === '127.0.0.1';
    if (!isLocalHost && /localhost|127\.0\.0\.1/.test(envBaseUrl)) {
      return '';
    }
  }
  return envBaseUrl;
};

/**
 * Fetch current user from backend
 * @returns {Promise<Object|null>} User object or null if not authenticated
 */
export const getUser = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const endpoint = `${baseUrl}/api/user`;
    console.log('API: Fetching user from', endpoint);
    const response = await fetch(endpoint, {
      method: 'GET',
      credentials: 'include', // Essential for session cookies over HTTPS
      headers: {
        'Content-Type': 'application/json',
      },
    });
    console.log('API: User fetch response status:', response.status);

    if (response.ok) {
      const user = await response.json();
      console.log('API: User data:', user);
      return user;
    }

    console.log('API: User fetch failed with status:', response.status);
    return null;
  } catch (error) {
    console.error('API: Error fetching user:', error);
    return null;
  }
};

/**
 * Check authentication status using /auth/me endpoint
 * @returns {Promise<{authenticated: boolean, user: Object|null}>}
 */
export const checkAuthStatus = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const endpoint = `${baseUrl}/api/auth/me`;
    console.log('API: Checking auth status from', endpoint);

    const response = await fetch(endpoint, {
      method: 'GET',
      credentials: 'include', // Essential for session cookies over HTTPS
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      console.log('API: Auth status:', data);
      return data;
    }

    // Handle specific error codes
    if (response.status === 401) {
      console.log('API: User not authenticated (401)');
      return { authenticated: false, user: null };
    }

    if (response.status === 404) {
      console.log('API: Auth endpoint not found (404), falling back to /api/user');
      // Fallback to /api/user endpoint if /api/auth/me doesn't exist
      return await getUser().then(user => ({
        authenticated: !!user,
        user: user
      })).catch(() => ({ authenticated: false, user: null }));
    }

    console.log('API: Auth check failed with status:', response.status);
    return { authenticated: false, user: null };
  } catch (error) {
    console.error('API: Error checking auth status:', error);

    // If network error, try fallback endpoint
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      console.log('API: Network error, trying fallback auth check');
      try {
        const fallbackResult = await getUser().then(user => ({
          authenticated: !!user,
          user: user
        })).catch(() => ({ authenticated: false, user: null }));
        return fallbackResult;
      } catch (fallbackError) {
        console.error('API: Fallback auth check also failed:', fallbackError);
      }
    }

    return { authenticated: false, user: null };
  }
};

/**
 * Fetch all inventory items
 * @returns {Promise<Array>} Array of inventory items
 */
export const getInventory = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/inventory`, {
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }

    // Get the actual error message from response
    let errorMessage = 'Failed to fetch inventory';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch (e) {
      errorMessage = response.statusText || `HTTP ${response.status}`;
    }

    throw new Error(`${errorMessage} (Status: ${response.status})`);
  } catch (error) {
    console.error('Error fetching inventory:', error);
    // If it's a network error, provide more context
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      throw new Error('Cannot connect to server. Please check if the backend is running.');
    }
    throw error;
  }
};

/**
 * Search inventory by query term
 * @param {string} query - Search term
 * @returns {Promise<Array>} Array of matching inventory items
 */
export const searchInventory = async (query) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/inventory/search?q=${encodeURIComponent(query)}`, {
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }

    // Get the actual error message from response
    let errorMessage = 'Failed to search inventory';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch (e) {
      errorMessage = response.statusText || `HTTP ${response.status}`;
    }

    throw new Error(`${errorMessage} (Status: ${response.status})`);
  } catch (error) {
    console.error('Error searching inventory:', error);
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      throw new Error('Cannot connect to server. Please check if the backend is running.');
    }
    throw error;
  }
};

/**
 * Create a new inventory item
 * @param {Object} data - Inventory item data
 * @returns {Promise<Object>} Created inventory item
 */
export const createInventory = async (data) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/inventory`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (response.ok) {
      const item = await response.json();
      return item;
    }
    const error = await response.json();
    throw new Error(error.error || 'Failed to create inventory item');
  } catch (error) {
    console.error('Error creating inventory item:', error);
    throw error;
  }
};

/**
 * Update an inventory item
 * @param {string} id - Inventory item ID
 * @param {Object} data - Updated data
 * @returns {Promise<Object>} Updated inventory item
 */
export const updateInventory = async (id, data) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/inventory/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (response.ok) {
      const item = await response.json();
      return item;
    }
    const error = await response.json();
    throw new Error(error.error || 'Failed to update inventory item');
  } catch (error) {
    console.error('Error updating inventory item:', error);
    throw error;
  }
};

/**
 * Delete an inventory item
 * @param {string} id - Inventory item ID
 * @returns {Promise<Object>} Success message
 */
export const deleteInventory = async (id) => {
  try {
    console.log('API: Deleting inventory item with ID:', id);
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/inventory/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      // Add timeout to prevent hanging requests
      signal: AbortSignal.timeout(10000), // 10 second timeout
    });

    console.log('API: Delete response status:', response.status);
    console.log('API: Delete response ok:', response.ok);

    // Read response as text first to avoid stream consumption issues
    const responseText = await response.text();
    console.log('API: Raw response (first 200 chars):', responseText.substring(0, 200));

    if (response.ok) {
      try {
        const result = JSON.parse(responseText);
        console.log('API: Delete success:', result);
        return result;
      } catch (jsonError) {
        console.log('API: Success response is not valid JSON');
        return { message: 'Item deleted successfully' };
      }
    }

    // Handle error responses
    let errorMessage = 'Failed to delete inventory item';

    // Check if response is HTML
    if (responseText.includes('<!DOCTYPE') || responseText.includes('<html>')) {
      console.log('API: Server returned HTML instead of JSON');
      errorMessage = `Server returned HTML instead of JSON. Status: ${response.status} ${response.statusText}`;
    } else {
      // Try to parse as JSON
      try {
        const errorData = JSON.parse(responseText);
        console.log('API: Delete error data:', errorData);
        errorMessage = errorData.error || errorMessage;
      } catch (jsonError) {
        console.log('API: Error response is not valid JSON');
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      }
    }

    throw new Error(errorMessage);
  } catch (error) {
    // Handle timeout errors specifically
    if (error.name === 'TimeoutError') {
      console.error('API: Delete request timed out');
      throw new Error('Request timed out. Please check your connection and try again.');
    }

    // Handle network errors
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      console.error('API: Network error during delete');
      throw new Error('Network error. Please check your internet connection.');
    }

    console.error('Error deleting inventory item:', error);
    throw error;
  }
};

// ===== AUTHENTICATION FUNCTIONS =====

/**
 * Logout user
 * @returns {Promise<Object>} Success message
 */
export const logout = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/auth/logout`, {
      method: 'GET',
      credentials: 'include',
      redirect: 'manual', // Don't follow redirects automatically
    });

    // Server redirects on successful logout, which is normal
    if (response.type === 'opaqueredirect' || response.status === 302 || response.status === 200) {
      // Logout successful - server redirected
      return { success: true, message: 'Logged out successfully' };
    }

    // If we get here, there was an error
    if (response.status >= 400) {
      let errorText = 'Unknown error';
      try {
        errorText = await response.text();
      } catch (e) {
        // Ignore text parsing errors
      }
      throw new Error(`Logout failed: ${response.status} ${response.statusText || errorText}`);
    }

    // Fallback success
    return { success: true, message: 'Logged out successfully' };
  } catch (error) {
    console.error('Error logging out:', error);
    throw error;
  }
};

/**
 * Local login authentication
 * @param {string} email - User email
 * @returns {Promise<Object>} Login result
 */
export const localLogin = async (email) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/auth/local-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });

    if (response.ok) {
      const result = await response.json();
      return result;
    }

    const error = await response.json();
    throw new Error(error.error || 'Login failed');
  } catch (error) {
    console.error('Error during local login:', error);
    throw error;
  }
};

/**
 * Get Google OAuth login URL
 * @returns {string} Google OAuth URL
 */
export const getGoogleLoginUrl = () => {
  const baseUrl = getApiBaseUrl();
  return `${baseUrl}/auth/google`;
};

// ===== INVENTORY FUNCTIONS =====

/**
 * Get inventory dropdown options
 * @param {string} type - Type of inventory (retail/wholesale)
 * @returns {Promise<Array>} Array of inventory options
 */
export const getInventoryDropdown = async (type) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/inventory/dropdown?type=${type}`, {
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }

    throw new Error('Failed to fetch inventory dropdown');
  } catch (error) {
    console.error('Error fetching inventory dropdown:', error);
    throw error;
  }
};

// ===== ORDER FUNCTIONS =====

/**
 * Create a new order
 * @param {Object} orderData - Order data
 * @returns {Promise<Object>} Created order
 */
export const createOrder = async (orderData) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/orders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(orderData),
    });

    if (response.ok) {
      const order = await response.json();
      return order;
    }

    const error = await response.json();
    throw new Error(error.details || error.error || 'Failed to create order');
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

/**
 * Get orders with pagination and filters
 * @param {Object} params - Query parameters (page, limit, status, type, search)
 * @returns {Promise<Object>} Orders data with pagination
 */
export const getOrders = async (params = {}) => {
  try {
    const baseUrl = getApiBaseUrl();
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const response = await fetch(`${baseUrl}/api/orders?${queryParams}`, {
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }

    throw new Error('Failed to fetch orders');
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

/**
 * Update order status
 * @param {string} orderId - Order ID
 * @param {string} status - New status
 * @returns {Promise<Object>} Updated order
 */
export const updateOrderStatus = async (orderId, status) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/orders/${orderId}/status`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ status }),
    });

    if (response.ok) {
      const result = await response.json();
      return result;
    }

    const error = await response.json();
    throw new Error(error.error || 'Failed to update order status');
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
};

/**
 * Add phone number to an order that was created without one
 * @param {string} orderId - Order ID
 * @param {string} phone - 10-digit Indian mobile
 * @returns {Promise<Object>} Updated order payload
 */
export const updateOrderPhone = async (orderId, phone) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/orders/${orderId}/phone`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ phone }),
    });

    if (response.ok) {
      return response.json();
    }

    const error = await response.json();
    throw new Error(error.error || 'Failed to update phone number');
  } catch (error) {
    console.error('Error updating order phone:', error);
    throw error;
  }
};

/**
 * Edit a pending retail order
 * @param {string} orderId
 * @param {Object} payload
 * @returns {Promise<Object>}
 */
export const updateRetailOrder = async (orderId, payload) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/orders/${orderId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      return response.json();
    }

    const error = await response.json();
    throw new Error(error.error || 'Failed to update order');
  } catch (error) {
    console.error('Error updating retail order:', error);
    throw error;
  }
};

/**
 * Permanently delete a cancelled order (advance no longer counted as retained)
 * @param {string} orderId
 * @returns {Promise<Object>}
 */
export const deleteCancelledOrder = async (orderId) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/orders/${orderId}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (response.ok) {
      return response.json();
    }

    const error = await response.json();
    throw new Error(error.error || 'Failed to delete order');
  } catch (error) {
    console.error('Error deleting cancelled order:', error);
    throw error;
  }
};

/**
 * Mark balance as received for an order
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} Success message
 */
export const markBalanceReceived = async (orderId) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/orders/${orderId}/balance-received`, {
      method: 'PUT',
      credentials: 'include',
    });

    if (response.ok) {
      const result = await response.json();
      return result;
    }

    const error = await response.json();
    throw new Error(error.error || 'Failed to mark balance as received');
  } catch (error) {
    console.error('Error marking balance received:', error);
    throw error;
  }
};

/**
 * Get order statistics summary
 * @returns {Promise<Object>} Order statistics
 */
export const getOrderStats = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/orders/stats/summary`, {
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }

    throw new Error('Failed to fetch order stats');
  } catch (error) {
    console.error('Error fetching order stats:', error);
    throw error;
  }
};

/**
 * Get dashboard statistics
 * @returns {Promise<Object>} Dashboard stats
 */
export const getDashboardStats = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/orders/dashboard/stats`, {
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }

    throw new Error('Failed to fetch dashboard stats');
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
};

/**
 * Get derived recent activity for overview dashboard
 * @returns {Promise<{ activities: Array }>}
 */
export const getDashboardActivity = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/orders/dashboard/activity`, {
      credentials: 'include',
    });

    if (response.ok) {
      return response.json();
    }

    throw new Error('Failed to fetch dashboard activity');
  } catch (error) {
    console.error('Error fetching dashboard activity:', error);
    throw error;
  }
};

/**
 * Trigger browser download of a PDF from base64 string
 */
export const downloadPdfFromBase64 = (base64, filename) => {
  downloadBlobFromBase64(base64, filename, 'application/pdf');
};

/**
 * Trigger browser download of any blob from base64 string
 */
export const downloadBlobFromBase64 = (base64, filename, mimeType) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Download receipt for an existing order
 * @param {string} orderId - Order ID
 * @param {'en'|'mr'|'kn'} [lang] - Optional language (en, mr, or kn)
 * @param {'jpeg'|'pdf'} [format] - Receipt format (default jpeg)
 * @returns {Promise<Object>} Receipt data
 */
export const fetchPublicOrderReceipt = async (orderId, lang, format = 'jpeg') => {
  try {
    const baseUrl = getApiBaseUrl();
    const params = new URLSearchParams();
    if (lang) params.set('lang', lang.toLowerCase());
    if (format) params.set('format', format.toLowerCase());
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await fetch(`${baseUrl}/api/orders/public/${orderId}/receipt${query}`);

    if (response.ok) {
      return response.json();
    }

    const error = await response.json();
    throw new Error(error.error || error.details || 'Failed to generate receipt');
  } catch (error) {
    console.error('Error fetching public receipt:', error);
    throw error;
  }
};

export const downloadOrderReceipt = async (orderId, lang, format = 'jpeg') => {
  try {
    const baseUrl = getApiBaseUrl();
    const params = new URLSearchParams();
    if (lang) params.set('lang', lang.toLowerCase());
    if (format) params.set('format', format.toLowerCase());
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await fetch(`${baseUrl}/api/orders/${orderId}/receipt${query}`, {
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }

    const error = await response.json();
    throw new Error(error.error || error.details || 'Failed to generate receipt');
  } catch (error) {
    console.error('Error downloading receipt:', error);
    throw error;
  }
};

/**
 * Get finance summary
 * @returns {Promise<Object>} Finance summary data
 */
export const getFinanceSummary = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/orders/finance/summary`, {
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }

    throw new Error('Failed to fetch finance summary');
  } catch (error) {
    console.error('Error fetching finance summary:', error);
    throw error;
  }
};

export const getReceiptWiseReport = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/orders/finance/receipt-report`, {
      credentials: 'include',
    });

    if (response.ok) {
      return response.json();
    }

    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch receipt-wise report');
  } catch (error) {
    console.error('Error fetching receipt-wise report:', error);
    throw error;
  }
};

export const getModelWiseReport = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/orders/finance/model-report`, {
      credentials: 'include',
    });

    if (response.ok) {
      return response.json();
    }

    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch model-wise report');
  } catch (error) {
    console.error('Error fetching model-wise report:', error);
    throw error;
  }
};

/**
 * Download full backup ZIP (inventory, orders, counters, photos, CSV reports).
 */
export const downloadFullBackupZip = async () => {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/api/backup/export`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.details || error.error || 'Failed to download backup');
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || `ekduntarts-backup-${new Date().toISOString().split('T')[0]}.zip`;

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Download ZIP of inventory idol photos with model code stamped on each image.
 * @param {'all'|'small'|'medium'|'large'|'pen'} [category='all']
 */
export const downloadInventoryPhotosZip = async (category = 'all') => {
  const baseUrl = getApiBaseUrl();
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  const query = params.toString() ? `?${params.toString()}` : '';

  const response = await fetch(`${baseUrl}/api/inventory/export-photos${query}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || error.details || 'Failed to download photos');
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename =
    match?.[1] ||
    `idol-photos-${category || 'all'}-${new Date().toISOString().split('T')[0]}.zip`;

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Merge-restore from a backup ZIP file (requires admin password).
 * @param {File} file
 * @param {string} password
 */
export const restoreFromBackupZip = async (file, password) => {
  const baseUrl = getApiBaseUrl();
  const formData = new FormData();
  formData.append('backup', file);
  formData.append('password', password);

  const response = await fetch(`${baseUrl}/api/backup/restore`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || data.details || 'Failed to restore backup');
  }
  return data;
};

// ===== PAYMENT FUNCTIONS =====

/**
 * Create a Razorpay payment order
 * @param {Object} paymentData - Payment data (amount, currency, receipt, notes)
 * @returns {Promise<Object>} Razorpay order details
 */
export const createPaymentOrder = async (paymentData) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/payments/create-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(paymentData),
    });

    if (response.ok) {
      const order = await response.json();
      return order;
    }

    const error = await response.json();
    throw new Error(error.error || 'Failed to create payment order');
  } catch (error) {
    console.error('Error creating payment order:', error);
    throw error;
  }
};

/**
 * Verify Razorpay payment
 * @param {Object} verificationData - Payment verification data
 * @returns {Promise<Object>} Verification result
 */
export const verifyPayment = async (verificationData) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/payments/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(verificationData),
    });

    if (response.ok) {
      const result = await response.json();
      return result;
    }

    const error = await response.json();
    throw new Error(error.error || 'Payment verification failed');
  } catch (error) {
    console.error('Error verifying payment:', error);
    throw error;
  }
};

/**
 * Simulate a test payment (sandbox only; requires existing Razorpay order id)
 * @param {Object} data - { razorpay_order_id }
 * @returns {Promise<Object>} Simulated payment credentials
 */
export const simulateTestPayment = async (data) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/payments/simulate-test`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (response.ok) {
      return response.json();
    }

    const error = await response.json();
    throw new Error(error.error || 'Failed to simulate test payment');
  } catch (error) {
    console.error('Error simulating test payment:', error);
    throw error;
  }
};

/**
 * Test payment gateway configuration
 * @returns {Promise<Object>} Test result
 */
export const testPaymentGateway = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/payments/test`, {
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }

    throw new Error('Failed to test payment gateway');
  } catch (error) {
    console.error('Error testing payment gateway:', error);
    throw error;
  }
};

// ===== LOGIN HISTORY FUNCTIONS =====

/**
 * Get login history with pagination
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Items per page (default: 20)
 * @returns {Promise<Object>} Login history data with pagination
 */
export const getLoginHistory = async (page = 1, limit = 20) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/auth/login-history?page=${page}&limit=${limit}`, {
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }

    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch login history');
  } catch (error) {
    console.error('Error fetching login history:', error);
    throw error;
  }
};

/**
 * Get login statistics
 * @returns {Promise<Object>} Login statistics
 */
export const getLoginStats = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/auth/login-stats`, {
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }

    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch login stats');
  } catch (error) {
    console.error('Error fetching login stats:', error);
    throw error;
  }
};

// ===== SESSION MANAGEMENT FUNCTIONS =====

/**
 * Extend user session by making an API call
 * @returns {Promise<Object>} Session extension result
 */
export const extendSession = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/auth/extend-session`, {
      method: 'POST',
      credentials: 'include',
    });

    if (response.ok) {
      const result = await response.json();
      return result;
    }

    throw new Error('Failed to extend session');
  } catch (error) {
    console.error('Error extending session:', error);
    throw error;
  }
};

/**
 * Get current session info including remaining time
 * @returns {Promise<Object>} Session info
 */
export const getSessionInfo = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/auth/session-info`, {
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }

    throw new Error('Failed to get session info');
  } catch (error) {
    console.error('Error getting session info:', error);
    throw error;
  }
};

// ===== SETTINGS (admin) =====

export const getOrderSequenceSettings = async () => {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/api/settings/order-sequences`, {
    credentials: 'include',
  });

  if (response.ok) {
    return response.json();
  }

  const error = await response.json().catch(() => ({}));
  throw new Error(error.error || 'Failed to fetch order sequence settings');
};

export const resetOrderCounters = async ({ password, types }) => {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/api/settings/order-sequences/reset-counters`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password, types }),
  });

  if (response.ok) {
    return response.json();
  }

  const error = await response.json().catch(() => ({}));
  throw new Error(error.error || 'Failed to reset order counters');
};

export const getPublicShopSettings = async () => {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/api/settings/shop/public`);

  if (response.ok) {
    return response.json();
  }

  const error = await response.json().catch(() => ({}));
  throw new Error(error.error || 'Failed to fetch shop settings');
};

export const getShopSettings = async () => {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/api/settings/shop`, {
    credentials: 'include',
  });

  if (response.ok) {
    return response.json();
  }

  const error = await response.json().catch(() => ({}));
  throw new Error(error.error || 'Failed to fetch shop settings');
};

export const updateShopSettings = async (patch = {}) => {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/api/settings/shop`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });

  if (response.ok) {
    return response.json();
  }

  const error = await response.json().catch(() => ({}));
  throw new Error(error.error || 'Failed to update shop settings');
};

export const submitShopInquiry = async (inquiryData) => {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/api/shop-inquiries`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(inquiryData),
  });

  if (response.ok) {
    return response.json();
  }

  const error = await response.json().catch(() => ({}));
  throw new Error(error.error || error.details || 'Failed to submit inquiry');
};

export const getShopInquiryCount = async (status = 'pending') => {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/api/shop-inquiries/count?status=${status}`, {
    credentials: 'include',
  });

  if (response.ok) {
    return response.json();
  }

  const error = await response.json().catch(() => ({}));
  throw new Error(error.error || 'Failed to fetch inquiry count');
};

export const getShopInquiries = async (params = {}) => {
  const baseUrl = getApiBaseUrl();
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });

  const response = await fetch(`${baseUrl}/api/shop-inquiries?${queryParams}`, {
    credentials: 'include',
  });

  if (response.ok) {
    return response.json();
  }

  const error = await response.json().catch(() => ({}));
  throw new Error(error.error || 'Failed to fetch shop inquiries');
};

export const approveShopInquiry = async (inquiryId, { totalAdvance, adminNote, settledPrice } = {}) => {
  const baseUrl = getApiBaseUrl();
  const body = { totalAdvance, adminNote };
  if (settledPrice !== undefined && settledPrice !== null && settledPrice !== '') {
    body.settledPrice = settledPrice;
  }
  const response = await fetch(`${baseUrl}/api/shop-inquiries/${inquiryId}/approve`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (response.ok) {
    return response.json();
  }

  const error = await response.json().catch(() => ({}));
  throw new Error(error.error || 'Failed to approve inquiry');
};

export const rejectShopInquiry = async (inquiryId, { reason } = {}) => {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/api/shop-inquiries/${inquiryId}/reject`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });

  if (response.ok) {
    return response.json();
  }

  const error = await response.json().catch(() => ({}));
  throw new Error(error.error || 'Failed to reject inquiry');
};

export const goLiveOrderReset = async ({ password, confirmText }) => {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/api/settings/order-sequences/go-live-reset`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password, confirmText }),
  });

  if (response.ok) {
    return response.json();
  }

  const error = await response.json().catch(() => ({}));
  throw new Error(error.error || 'Failed to reset orders for go-live');
};

export const clearAllBusinessData = async ({ password, confirmText }) => {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/api/settings/clear-all-data`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password, confirmText }),
  });

  if (response.ok) {
    return response.json();
  }

  const error = await response.json().catch(() => ({}));
  throw new Error(error.error || 'Failed to clear all photos and data');
};
